import {FileObject} from "../UploadUtils.ts";
import {ensureConnection} from "../../websocket/websocketUtils.ts";

const uploadToCloudConvert = async (file: File, cloudConvertData: any, updateProgress: (name: string, progress: number) => void) => {
    const formData = new FormData();

    // Add all fields from CloudConvert form parameters
    Object.entries(cloudConvertData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
    });

    // Add the file directly - much more efficient than base64
    formData.append('file', file);

    // Upload the file with progress tracking
    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                updateProgress("uploading", progress);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.response);
            } else {
                const errorMessage = xhr.status === 0 ? 'Network connection lost during upload' :
                                   xhr.status === 413 ? 'File too large to upload' :
                                   xhr.status === 503 ? 'Upload service temporarily unavailable' :
                                   `Upload failed with status ${xhr.status}`;
                reject(new Error(errorMessage));
            }
        };

        xhr.onerror = () => {
            // Network error, connection lost, or CORS issue
            reject(new Error('Network connection lost during upload'));
        };

        xhr.open('POST', cloudConvertData.url);
        xhr.send(formData);
    });
};

/**
 * Fetches and reads the contents of a URL as text
 */
const fetchUrlContents = async (url: string): Promise<string> => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch URL contents: ${response.statusText}`);
    }
    return response.text();
};

export const handleCloudConvertEvents = async (args: any, dynamicStateObjRef: any, updateSelectedMedia: any, setDynamicStateObj?: any) => {
    const fileId = args.fileId;
    const updateProgress = (name: string, progress: number) => {
        updateSelectedMedia(fileId, {loadingInfo: {task: name, progress}}, "update");
    }
    const originalFile = dynamicStateObjRef.current.selectedMedia[fileId].originalFile;
    const fileType = dynamicStateObjRef.current.selectedMedia[fileId].type;

    switch (args.type) {
        case 'getUploadSignedUrl':
            try {
                await uploadToCloudConvert(originalFile, args, updateProgress);
            } catch (error) {
                console.error('Upload failed:', error);
                updateSelectedMedia(fileId, null, "delete");

                // Show user-facing error message if setDynamicStateObj is available
                if (setDynamicStateObj) {
                    const errorMessage = error instanceof Error ? error.message : 'Upload failed due to unknown error';
                    setDynamicStateObj({
                        visiblePopup: "error",
                        errorPopupMessage: `Failed to upload "${originalFile.name}": ${errorMessage}`
                    });
                }
            }
            break;
        case 'taskCreated':
        case 'taskProgress':
        case 'taskFinished':
            updateProgress(args.task, args.percent);
            break;
        case 'thumbnailFinished':
            updateSelectedMedia(fileId, {
                previewUrl: args.fileUrl,
                loadingInfo: {task: args.task || "done"}
            }, "update");
            break;
        case 'uploadFinished':
            const updateArgs: any = {
                loadingInfo: {task: "done"},
                fileName: args.fileName
            };
            if (args.isText) {
                updateArgs.fileContent = await fetchUrlContents(args.fileUrl);
            }
            else if (fileType === "video") {
                updateArgs.fileContent = args.fileUrl;
            }
            else if (fileType === "audio") {
                updateArgs.fileContent = args.fileUrl;
                updateArgs.transcript = args.transcript;
            }
            else {
                updateArgs.fileContent = args.fileUrl;
                updateArgs.previewUrl = args.fileUrl;
            }

            updateSelectedMedia(fileId, updateArgs, "update");
            break;
        case 'taskFailed':
            console.error('CloudConvert task failed:', args.data);
            updateSelectedMedia(fileId, null, "delete");

            // Show user-facing error message if setDynamicStateObj is available
            if (setDynamicStateObj) {
                setDynamicStateObj({
                    visiblePopup: "error",
                    errorPopupMessage: `Failed to process "${originalFile.name}": Processing failed on server`
                });
            }
            break;
    }
};

export const uploadFileToServer = async (
    fileObject: FileObject,
    user: any,
    isIncognito: boolean,
    updateSelectedMedia: any,
    websocketRef: any,
    setupWebsocketHelper: any,
    setDynamicStateObj?: any
) => {
    try {
        await ensureConnection(websocketRef, setupWebsocketHelper);

        const bucketName = (!user || isIncognito) ? "temp" : user.uid;
        websocketRef.current.send(JSON.stringify({
            functionName: "uploadEvent",
            args: {
                event: "getUploadSignedUrl",
                fileId: fileObject.fileId,
                bucketName: bucketName,
                type: fileObject.type
            }
        }));
    } catch (error) {
        console.error('Upload failed:', error);
        updateSelectedMedia(fileObject.fileId, null, "delete");

        // Show user-facing error message if setDynamicStateObj is available
        if (setDynamicStateObj) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to establish connection';
            setDynamicStateObj({
                visiblePopup: "error",
                errorPopupMessage: `Failed to upload "${fileObject.fileName}": ${errorMessage}`
            });
        }
    }
};